import * as THREE from 'three';
import styles from './styles.module.scss';
import { useDailySunlightStore } from '@/store/dailySunlightsStore';
import { DailySunlightResult } from '@/types/sunlight';
import { WeaveInput } from '../UI/WeaveInput/WeaveInput';
import { WeaveCheckbox } from '../UI/WeaveCheckbox/WeaveCheckbox';
import { useEffect, useState } from 'react';
import { WorkflowProps } from '@/types/workflow';
import { FacadeSunlightControls } from '../FacadeSunlightControls/FacadeSunlightControls';
import { DailySunlightControls } from './DailySunlightControls';
import { AnimationControls } from './AnimationControls';
import { WeaveAccordion } from '../UI/WeaveAccordion/WeaveAccordion';
import { WeaveButton } from '../UI/WeaveButton/WeaveButton';
import { DatePicker } from '../UI/DatePicker/DatePicker';
import { FormaTab, FormaTabs } from '../UI/FormaTabs';
import { useWorkflowStore } from '@/store/workflowStore';

interface SunlightControlsProps {
	calculateDailySunlightForRooms: (
		selectedFloorIndex?: number | null
	) => Promise<DailySunlightResult[] | undefined>;
	calculateDailySunlightForSelectedGridPoints: () => Promise<DailySunlightResult[] | undefined>;
	availableFloorIndices: number[];
	onMetricChange: (metric: keyof DailySunlightResult) => void;
	calculatingDailySunlight: boolean;
	clearAllVisualizations: () => void;
	scene: THREE.Scene;
	workflowProps: WorkflowProps;
	// Facade sunlight props
	calculateFacadeSunlight: () => Promise<void>;
	clearFacadeVisualization: () => void;
	calculatingFacadeSunlight: boolean;
	facadeSunlightEnabled: boolean;
	startFacadeAnimation: () => Promise<void>;
	stopFacadeAnimation: () => void;
	isAnimating: boolean;
	calculateSingleDay: (dayIndex: number) => Promise<void>;
	// Daily sunlight animation props
	startDailyAnimation: () => Promise<void>;
	stopDailyAnimation: () => void;
	isAnimatingDaily: boolean;
	calculateSingleDayDaily: (dayIndex: number) => Promise<void>;
}

export const SunlightControls = (props: SunlightControlsProps) => {
	const {
		startDate,
		setStartDate,
		endDate,
		setEndDate,
		gridSize,
		setGridSize,
		useApproximation,
		setUseApproximation,
		setSunlightResultsRooms,
		setSunlightResultsGridPoints,
	} = useDailySunlightStore();
	const {
		calculateDailySunlightForRooms,
		calculateDailySunlightForSelectedGridPoints,
		availableFloorIndices,
		calculatingDailySunlight,
		onMetricChange,
		clearAllVisualizations,
		calculateFacadeSunlight,
		clearFacadeVisualization,
		calculatingFacadeSunlight,
		facadeSunlightEnabled,
		startFacadeAnimation,
		stopFacadeAnimation,
		isAnimating,
		calculateSingleDay,
		startDailyAnimation,
		stopDailyAnimation,
		isAnimatingDaily,
		calculateSingleDayDaily,
	} = props;

	const { currentStage } = useWorkflowStore();
	const [selectedTab, setSelectedTab] = useState<'daily' | 'facade'>(
		currentStage === 'predesign_programming' ? 'facade' : 'daily'
	);
	const [isDailyTabDisabled, setIsDailyTabDisabled] = useState(currentStage === 'predesign_programming');

	const handleClearAllVisualizations = (selectedTab: 'daily' | 'facade') => {
		if (selectedTab === 'daily') {
			clearAllVisualizations();
			setSunlightResultsRooms(null);
			setSunlightResultsGridPoints(null);
		} else {
			clearFacadeVisualization();
		}
	};

	const handleTabChange = (event: unknown) => {
		const target = event as {
			nativeEvent?: { detail?: { tabContentId?: string } };
			detail?: { tabContentId?: string };
		};
		const newTab =
			target?.nativeEvent?.detail?.tabContentId ?? target?.detail?.tabContentId;
		if (newTab !== 'daily' && newTab !== 'facade') return;

		setSelectedTab(newTab);
		clearAllVisualizations();

		if (newTab === 'daily') {
			if (isAnimating) {
				stopFacadeAnimation();
			}
		} else {
			if (isAnimatingDaily) {
				stopDailyAnimation();
			}
			clearFacadeVisualization();
		}
	};

	useEffect(() => {
		if (currentStage === 'predesign_programming') {
			setSelectedTab('facade');
			setIsDailyTabDisabled(true);
		} else {
			setSelectedTab('daily');
			setIsDailyTabDisabled(false);
		}
	}, [currentStage]);

	return (
		<div className={styles.container}>
			<WeaveAccordion label="Sunlight Settings">
				<div className={styles.inputControls}>
					<div className={styles.datePickers}>
						<label>Start:</label>
						<DatePicker date={startDate} onDateChange={setStartDate} />
					</div>
					<div className={styles.datePickers}>
						<label>End:</label>
						<DatePicker date={endDate} onDateChange={setEndDate} />
					</div>
					<div className={styles.controlsRow}>
						<label>
							Grid Size:
							<WeaveInput
								label="Grid size"
								type="number"
								value={gridSize}
								style={{ width: '50px' }}
								onChange={(event: unknown) => {
									const target = event as {
										nativeEvent: { detail: { value: string } };
									};
									const value = target.nativeEvent.detail.value;
									setGridSize(Number(value));
								}}
							/>
						</label>
						<WeaveCheckbox
							label="Approximation"
							showLabel={true}
							checked={useApproximation}
							change={(event: unknown) => {
								const target = event as {
									nativeEvent: { detail: { checked: boolean } };
								};
								setUseApproximation(target.nativeEvent.detail.checked);
							}}
						/>
					</div>
				</div>
			</WeaveAccordion>
			<div className={styles.controlsRow}>
				<FormaTabs
					gap={8}
					density="medium"
					onChange={(event) => handleTabChange(event)}
					selectedtab={isDailyTabDisabled ? 1 : 0}
				>
					<FormaTab for="daily" label="Daily Sunlight" disabled={isDailyTabDisabled} />
					<div id="daily" slot="content">
						<div className={styles.tabContent}>
							<DailySunlightControls
								calculateDailySunlightForRooms={calculateDailySunlightForRooms}
								calculateDailySunlightForSelectedGridPoints={
									calculateDailySunlightForSelectedGridPoints
								}
								availableFloorIndices={availableFloorIndices}
								calculatingDailySunlight={calculatingDailySunlight}
								onMetricChange={onMetricChange}
							/>
							<WeaveButton
								onClick={() => handleClearAllVisualizations(selectedTab)}
								disabled={calculatingFacadeSunlight || calculatingDailySunlight}
							>
								Clear Visualization
							</WeaveButton>
						</div>
					</div>
					<FormaTab for="facade" label="Facade Sunlight" />
					<div id="facade" slot="content">
						<div className={styles.tabContent}>
							<FacadeSunlightControls
								calculateFacadeSunlight={calculateFacadeSunlight}
								clearFacadeVisualization={clearFacadeVisualization}
								calculatingFacadeSunlight={calculatingFacadeSunlight}
							/>
							<WeaveButton
								onClick={() => handleClearAllVisualizations(selectedTab)}
								disabled={calculatingFacadeSunlight || calculatingDailySunlight}
							>
								Clear Visualization
							</WeaveButton>
						</div>
					</div>
				</FormaTabs>
			</div>
			<WeaveAccordion label="Animation Controls">
				<AnimationControls
					animationType={selectedTab}
					startDailyAnimation={startDailyAnimation}
					stopDailyAnimation={stopDailyAnimation}
					isAnimatingDaily={isAnimatingDaily}
					calculateSingleDayDaily={calculateSingleDayDaily}
					clearDailyVisualization={clearAllVisualizations}
					startFacadeAnimation={startFacadeAnimation}
					stopFacadeAnimation={stopFacadeAnimation}
					isAnimatingFacade={isAnimating}
					calculateSingleDayFacade={calculateSingleDay}
					clearFacadeVisualization={clearFacadeVisualization}
					calculatingInProgress={calculatingDailySunlight || calculatingFacadeSunlight}
					facadeSunlightEnabled={facadeSunlightEnabled}
				/>
			</WeaveAccordion>
		</div>
	);
};
